import {
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { logOut } from '../slices/authorizationSlice';
import { handleUnauthorized } from '../../lib/handleUnauthorized';

/**
 * REACT_APP_API_HOST may be origin only (http://host:port) or a full API base
 * (http://host:port/v1/ or .../v1/admin-panel/). Also tolerates http:typo → http://
 */
function normalizeReactAppApiBaseUrl(): string {
  let raw = (process.env.REACT_APP_API_HOST || 'http://localhost:4000').trim();

  // Local API runs plain HTTP; https://localhost breaks fetch (no TLS on Nest).
  if (/^https:\/\/localhost(?::\d+)?/i.test(raw)) {
    raw = raw.replace(/^https:/i, 'http:');
  }

  if (/^https?:[^/]/i.test(raw)) {
    raw = raw
      .replace(/^http:(?!\/\/)/i, 'http://')
      .replace(/^https:(?!\/\/)/i, 'https://');
  }

  try {
    const url = new URL(raw);
    let path = url.pathname.replace(/\/+$/, '') || '';
    if (path === '') {
      path = '/v1';
    }
    url.pathname = `${path}/`;
    return url.toString();
  } catch {
    const base = raw.replace(/\/+$/, '');
    return `${base}/v1/`;
  }
}

const API_BASE_URL = normalizeReactAppApiBaseUrl();

/**
 * RTK Query base query with automatic 401 handling.
 *
 * When any endpoint returns HTTP 401 the user is logged out immediately and
 * redirected to the sign-in page — one centralised place, zero duplication
 * across individual API slices.
 *
 * Usage in createApi:
 *   baseQuery: baseQueryWithReauth
 */
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const rawBaseQuery = fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as any).authorization?.accessToken;
      if (token) headers.set('Authorization', `Bearer ${token}`);
      return headers;
    },
  });

  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    api.dispatch(logOut(undefined));
    handleUnauthorized();
  }

  return result;
};
